(() => {
  const reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');
  document.querySelectorAll('[data-be-immersive]').forEach((root) => {
    const scenes = [...root.querySelectorAll('[data-be-scene]')];
    const buttons = [...root.querySelectorAll('[data-be-scene-button]')];
    const panels = [...root.querySelectorAll('[data-be-product-panel]')];
    const stage = root.querySelector('[data-be-scenes]');
    const drawer = root.querySelector('[data-be-product-drawer]');
    const scrim = root.querySelector('[data-be-product-close][hidden]');
    const menu = root.querySelector('[data-be-mobile-menu]');
    const counter = root.querySelector('[data-be-scene-count]');
    let index = 0;
    let dragging = false;
    let startX = 0;
    let lastX = 0;
    let rotateX = 0;
    let rotateY = 0;
    let lastFocus = null;
    let tapProductIndex = null;
    let movedDuringDrag = false;

    const applyOrbit = () => {
      const orbit = scenes[index]?.querySelector('[data-be-orbit]');
      if (!orbit || reducedMotion.matches) return;
      orbit.style.setProperty('--be-rx', `${rotateX}deg`);
      orbit.style.setProperty('--be-ry', `${rotateY}deg`);
    };

    const setScene = (nextIndex, announce = true) => {
      if (!scenes.length) return;
      index = (nextIndex + scenes.length) % scenes.length;
      scenes.forEach((scene, sceneIndex) => {
        const active = sceneIndex === index;
        scene.classList.toggle('is-active', active);
        scene.setAttribute('aria-hidden', active ? 'false' : 'true');
      });
      buttons.forEach((button, buttonIndex) => {
        const active = buttonIndex === index;
        button.classList.toggle('is-active', active);
        button.setAttribute('aria-current', active ? 'true' : 'false');
      });
      if (counter) counter.textContent = `${String(index + 1).padStart(3, '0')} / ${String(scenes.length).padStart(3, '0')}`;
      rotateX = 0;
      rotateY = 0;
      applyOrbit();
      if (announce) root.dispatchEvent(new CustomEvent('burntedge:scenechange', { detail: { index } }));
    };

    const closeMenu = () => {
      menu?.classList.remove('is-open');
      menu?.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('be-menu-open');
    };

    const openDrawer = (panelIndex = index, trigger = null) => {
      lastFocus = trigger || document.activeElement;
      panels.forEach((panel, panelPosition) => { panel.hidden = panelPosition !== panelIndex; });
      drawer?.classList.add('is-open');
      drawer?.setAttribute('aria-hidden', 'false');
      if (scrim) scrim.hidden = false;
      document.body.classList.add('be-drawer-open');
      closeMenu();
      requestAnimationFrame(() => drawer?.querySelector('[data-be-product-close]')?.focus());
    };

    const closeDrawer = () => {
      drawer?.classList.remove('is-open');
      drawer?.setAttribute('aria-hidden', 'true');
      if (scrim) scrim.hidden = true;
      document.body.classList.remove('be-drawer-open');
      lastFocus?.focus?.();
    };

    buttons.forEach((button) => button.addEventListener('click', () => setScene(Number(button.dataset.index))));
    root.querySelector('[data-be-prev]')?.addEventListener('click', () => setScene(index - 1));
    root.querySelector('[data-be-next]')?.addEventListener('click', () => setScene(index + 1));
    root.querySelector('[data-be-explore]')?.addEventListener('click', () => { setScene(index + 1); stage?.focus(); });
    root.querySelectorAll('[data-be-focus-scenes]').forEach((button) => button.addEventListener('click', () => { closeMenu(); stage?.focus(); }));
    root.querySelectorAll('[data-be-current-product]').forEach((button) => button.addEventListener('click', () => openDrawer(index, button)));
    root.querySelectorAll('[data-be-product-open]').forEach((button) => button.addEventListener('click', () => openDrawer(Number(button.dataset.index), button)));
    root.querySelectorAll('[data-be-product-close]').forEach((button) => button.addEventListener('click', closeDrawer));
    root.querySelector('[data-be-menu]')?.addEventListener('click', () => {
      menu?.classList.add('is-open');
      menu?.setAttribute('aria-hidden', 'false');
      document.body.classList.add('be-menu-open');
    });
    root.querySelector('[data-be-menu-close]')?.addEventListener('click', closeMenu);

    const dragStart = (x, productIndex = null) => {
      dragging = true;
      movedDuringDrag = false;
      tapProductIndex = productIndex;
      startX = x;
      lastX = x;
    };
    const dragMove = (x, y) => {
      if (!dragging) return;
      if (Math.abs(x - startX) > 10) movedDuringDrag = true;
      rotateX = Math.max(-11, Math.min(11, rotateX + (x - lastX) * 0.045));
      rotateY = Math.max(-5, Math.min(5, y * 0.01));
      lastX = x;
      applyOrbit();
    };
    const dragEnd = (x) => {
      if (!dragging) return;
      dragging = false;
      const distance = x - startX;
      const tappedProductIndex = tapProductIndex;
      tapProductIndex = null;

      if (Math.abs(distance) > 62) {
        setScene(index + (distance < 0 ? 1 : -1));
        return;
      }

      if (!movedDuringDrag && tappedProductIndex !== null) {
        openDrawer(tappedProductIndex, scenes[tappedProductIndex]?.querySelector('[data-be-orbit]'));
        return;
      }

      rotateX *= 0.25;
      rotateY *= 0.25;
      applyOrbit();
    };

    stage?.addEventListener('pointerdown', (event) => {
      if (event.target.closest('button, a, input, select, label')) return;
      const orbit = event.target.closest('[data-be-orbit]');
      const scene = event.target.closest('[data-be-scene]');
      const productIndex = orbit && scene ? Number(scene.dataset.index) : null;
      stage.setPointerCapture?.(event.pointerId);
      dragStart(event.clientX, Number.isFinite(productIndex) ? productIndex : null);
    });
    stage?.addEventListener('pointermove', (event) => dragMove(event.clientX, event.clientY - innerHeight / 2));
    stage?.addEventListener('pointerup', (event) => dragEnd(event.clientX));
    stage?.addEventListener('pointercancel', (event) => dragEnd(event.clientX));
    stage?.addEventListener('mousemove', (event) => {
      if (dragging || matchMedia('(pointer: coarse)').matches || reducedMotion.matches) return;
      rotateX = (event.clientX / innerWidth - 0.5) * 7;
      rotateY = -(event.clientY / innerHeight - 0.5) * 4;
      applyOrbit();
    });
    stage?.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowRight') setScene(index + 1);
      if (event.key === 'ArrowLeft') setScene(index - 1);
    });

    root.querySelectorAll('[data-be-product-form]').forEach((form) => {
      const variantsNode = form.querySelector('[data-be-variants]');
      const variantInput = form.querySelector('[data-be-variant-id]');
      const addButton = form.querySelector('[data-be-add]');
      const priceNode = form.closest('[data-be-product-panel]')?.querySelector('[data-be-price]');
      if (!variantsNode || !variantInput || !addButton) return;
      const variants = JSON.parse(variantsNode.textContent);
      const updateVariant = () => {
        const selected = [...form.querySelectorAll('.be-option')].map((group) => group.querySelector('input:checked')?.value);
        const variant = variants.find((item) => item.options.every((value, position) => value === selected[position]));
        if (!variant) {
          addButton.disabled = true;
          addButton.textContent = 'COMBINATION UNAVAILABLE';
          return;
        }
        variantInput.value = variant.id;
        addButton.disabled = !variant.available;
        addButton.textContent = variant.available ? 'ADD TO BAG' : 'CURRENTLY UNAVAILABLE';
        if (priceNode && typeof variant.price === 'number') priceNode.textContent = new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(variant.price / 100);
      };
      form.addEventListener('change', updateVariant);
    });

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') { closeDrawer(); closeMenu(); }
    });
    setScene(0, false);
  });
})();
